import { useCallback, useEffect, useSyncExternalStore } from "react";

import {
  endDeckSession,
  getDeckSession,
  startDeckSession,
  subscribeDeckSession,
  takeOverDeck,
} from "@/lib/deckSession";

export interface DeckSessionHandle {
  // False until the lock outcome is known for THIS deck. The editor must not paint before
  // then — it would come up editable and then flip to read-only under the user's hands.
  ready: boolean;
  // This page owns the deck and may write (TODO §12.5.1).
  writable: boolean;
  // No Web Locks here, so nothing is actually enforcing single-writer (§12.5.5).
  degraded: boolean;
  // §12.5.2 "Edit here": ask the current holder to stand down. Resolves true once this page
  // holds the lock — the caller should then re-hydrate, since the deck has moved on.
  takeOver: () => Promise<boolean>;
}

// React's view of the module-level deck session (`src/lib/deckSession.ts`). The session is
// deliberately NOT React state: the persistence listener consults it from outside the tree,
// on every dispatched action, and it must survive the editor subtree remounting on a deck
// switch. `useSyncExternalStore` just subscribes the UI to it.
export function useDeckSession(deckId: string): DeckSessionHandle {
  const session = useSyncExternalStore(subscribeDeckSession, getDeckSession);

  useEffect(() => {
    void startDeckSession(deckId);
    return () => endDeckSession();
  }, [deckId]);

  const takeOver = useCallback(() => takeOverDeck(), []);

  // A session for a *different* deck is a session in transition — the effect above has not
  // run for this deckId yet. Report not-ready rather than leaking the previous deck's answer.
  const ready = session.settled && session.deckId === deckId;
  return {
    ready,
    writable: ready && session.writable,
    degraded: session.degraded,
    takeOver,
  };
}
